import { useState } from 'react';
import { Violet300 } from '@zep-us/zus/tokens';
import { nanoid } from 'nanoid';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { BookFillIcon, CheckSmallCircleFillIcon } from '@/icons';
import { registerModalCancellation } from '@/shared/utils/modalCancellation';
import { Button, DialogBody, DialogFooter, DialogHeader, ResponsiveDialog, type ResponsiveDialogProps } from '@/ui_v3';
import { type LearningMaterial } from '@/views/build/modal/LearningMaterialUploadModal';
import { FileViewer } from './FileViewer';
const overlayId = nanoid(8);
let cancelActive: (() => void) | undefined;
let activeSource: LearningMaterialModalSource | undefined;
type LearningMaterialModalSource = 'build' | 'quizDetail' | 'playReadyAlert' | 'playObject' | 'playHeader';
export const learningMaterialFileModal = {
  async openAsync(args: {
    file: Pick<LearningMaterial, 'url' | 'fileSize'>;
    alwaysHasCloseButton?: boolean;
    source: LearningMaterialModalSource;
  }) {
    if (cancelActive) {
      logger.debug('LearningMaterialFileModal: duplicate open skipped', {
        source: args.source,
        activeSource
      });
      return undefined;
    }
    let closePending: ((value: boolean | undefined) => void) | undefined;
    let resolveCancellation!: (value: undefined) => void;
    const cancelled = new Promise<undefined>(resolve => {
      resolveCancellation = resolve;
    });
    const cancel = () => {
      if (cancelActive !== cancel) return;
      closePending?.(undefined);
      overlay.unmount(overlayId);
      cancelActive = undefined;
      unregister();
      resolveCancellation(undefined);
    };
    cancelActive = cancel;
    activeSource = args.source;
    const unregister = registerModalCancellation(cancel);
    try {
      const pending = overlay.openAsync<boolean | undefined>(({
        close
      }) => {
        closePending = close;
        return <LearningMaterialFileModal onClose={value => {
          if (cancelActive === cancel) close(value === true);
        }} file={args.file} alwaysHasCloseButton={args.alwaysHasCloseButton} />;
      }, {
        overlayId
      });
      const result = await Promise.race([pending, cancelled]);
      return cancelActive === cancel ? result : undefined;
    } catch (error) {
      logger.captureError(error, {
        fingerprint: 'overlay-kit-modal-error',
        extra: {
          overlayId,
          modalName: 'LearningMaterialFileModal'
        }
      });
      return false;
    } finally {
      unregister();
      // A cancelled request must not remove a newer modal with the same ID.
      if (cancelActive === cancel) {
        overlay.unmount(overlayId);
        cancelActive = undefined;
        activeSource = undefined;
      }
    }
  },
  unmount() {
    if (cancelActive) cancelActive();else overlay.unmount(overlayId);
  }
};
export const LearningMaterialFileModal = (_props: Pick<ResponsiveDialogProps, 'onClose'> & {
  file: Pick<LearningMaterial, 'url' | 'fileSize'>;
  alwaysHasCloseButton?: boolean;
}) => {
  const {
    file,
    ...props
  } = _props;
  const {
    t
  } = useTranslation(['common', 'play']);
  const [hasFooter, setHasFooter] = useState(false);
  const [hasCloseButton, setHasCloseButton] = useState(true);
  return <ResponsiveDialog {...props} className="h-[90%]" size="2xl" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="LearningMaterialFileModal" data-sentry-source-file="LearningMaterialFileModal.tsx">
      <DialogHeader title={t('common:header.learningMaterial.button')} hasCloseButton={hasCloseButton} leftSlot={<div className="flex size-[36px] items-center justify-center">
            <BookFillIcon size="md" fill={Violet300} />
          </div>} onClose={() => props.onClose?.(false)} data-sentry-element="DialogHeader" data-sentry-source-file="LearningMaterialFileModal.tsx" />
      <DialogBody data-sentry-element="DialogBody" data-sentry-source-file="LearningMaterialFileModal.tsx">
        <FileViewer file={file} onLoad={() => {
        setHasFooter(true);
        if (!props.alwaysHasCloseButton) {
          setHasCloseButton(false);
        }
      }} data-sentry-element="FileViewer" data-sentry-source-file="LearningMaterialFileModal.tsx" />
      </DialogBody>
      {hasFooter && <DialogFooter>
          <Footer onComplete={() => props.onClose?.(true)} />
        </DialogFooter>}
    </ResponsiveDialog>;
};
const Footer = ({
  onComplete
}: {
  onComplete: () => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'play']);
  return <div className="flex justify-end" data-sentry-component="Footer" data-sentry-source-file="LearningMaterialFileModal.tsx">
      <Button className="max-mobile-lg:w-full" size="lg" variant="contained" intent="strong" leftSlot={<CheckSmallCircleFillIcon className="fill-white" size="md" />} onClick={onComplete} data-sentry-element="Button" data-sentry-source-file="LearningMaterialFileModal.tsx">
        {t('common:learningMaterialModal.complete')}
      </Button>
    </div>;
};